import { Link } from 'react-router-dom'
import { HalamanLegal, Pasal, Surel } from '../components/HalamanLegal'

/**
 * Ketentuan layanan orisini.com — berlaku untuk seluruh produk Orisini.
 *
 * Pasal "risiko platform" adalah yang paling penting dibaca calon user:
 * Orisini tidak memakai API resmi, jadi akun platform user bisa dibatasi
 * oleh platformnya dan kita tidak bisa mencegahnya. Jangan diperhalus.
 */
export default function TermsPage() {
  const tautan = 'text-foreground underline underline-offset-4'

  return (
    <HalamanLegal
      judul="Terms of Service"
      ringkas="These terms are the agreement between you and Orisini for using orisini.com, every Orisini product subdomain, the Orisini Agent desktop app, and the Orisini browser extensions. By creating an account you accept them."
    >
      <Pasal id="service" judul="1. What Orisini is">
        <p>
          Orisini helps you create social media content and publish it on a schedule. The schedule
          lives on our servers; the actual publishing is done by the Orisini Agent on your own
          computer, which operates each platform through a real Chrome browser, the same way you
          would by hand.
        </p>
        <p>
          <strong>Orisini does not use any platform's official API</strong> and is not affiliated
          with, endorsed by, or sponsored by Pinterest, Meta (Instagram, Threads, Facebook), TikTok,
          or any other platform. Their names are trademarks of their owners.
        </p>
      </Pasal>

      <Pasal id="platform-risk" judul="2. Platform risk — please read">
        <p>
          Social platforms set their own rules, and some restrict automated activity. Using Orisini
          to post on your behalf may be treated by a platform as automation.{' '}
          <strong>
            A platform may limit, suspend, or close your account there, and we cannot prevent or
            reverse that.
          </strong>{' '}
          You are responsible for following each platform's terms and for how often and what you
          post.
        </p>
        <p>
          Platforms also change their interfaces without notice. When they do, posting can fail or
          behave differently until we adapt. A failed attempt may leave a draft on your platform
          account.
        </p>
      </Pasal>

      <Pasal id="account" judul="3. Your account">
        <ul>
          <li>You must be at least 18 years old.</li>
          <li>Give a real email address and keep your password to yourself.</li>
          <li>One person per account. You're responsible for everything done under it.</li>
          <li>
            Only connect social media accounts you own or are authorized to manage, and only install
            the agent on computers you control.
          </li>
        </ul>
      </Pasal>

      <Pasal id="agent" judul="4. The agent and your computer">
        <p>
          Scheduled posts publish only while your computer is on, online, and running the agent.
          If it isn't, the post waits and publishes once the agent is back. Publishing times are a
          target, not a guarantee.
        </p>
        <p>
          The agent updates itself automatically. You can revoke any device at any time in{' '}
          <Link to="/devices" className={tautan}>
            Devices
          </Link>
          .
        </p>
      </Pasal>

      <Pasal id="content" judul="5. Your content">
        <p>
          You keep ownership of everything you upload or create with Orisini. You give us a limited
          permission to store, process, render, and transmit it only as needed to run the service
          for you — including sending it to the AI providers listed in the{' '}
          <Link to="/privacy" className={tautan}>
            Privacy Policy
          </Link>{' '}
          when you use AI features.
        </p>
        <p>
          You are responsible for having the rights to what you post, including images, music,
          and text, and for checking AI-generated content before it is published. AI output can be
          wrong.
        </p>
      </Pasal>

      <Pasal id="acceptable-use" judul="6. What you may not do">
        <ul>
          <li>post spam, or content that is illegal, deceptive, hateful, or infringes others' rights;</li>
          <li>impersonate others or run accounts you aren't authorized to manage;</li>
          <li>harass people, or use Orisini for mass unsolicited messaging;</li>
          <li>try to access other users' data, bypass plan limits, or probe our systems without permission;</li>
          <li>resell Orisini or share one account between multiple people or businesses.</li>
        </ul>
        <p>We may remove content or suspend accounts that break these rules.</p>
      </Pasal>

      <Pasal id="plans" judul="7. Plans and payment">
        <p>
          The free plan is free. Paid plans are billed in Indonesian rupiah, per month or per year,
          at the price shown when you order. Limits on posts, connected accounts, and generated
          content are totals across all Orisini products.
        </p>
        <p>
          Payment is by QRIS. Payments are verified manually, so a subscription may activate some
          time after you pay rather than instantly.
        </p>
        <p>
          If you cancel, your plan stays active until the end of the period you've paid for, then
          returns to the free plan. Payments are not refundable, except where the law requires it
          or where we fail to activate a plan you paid for.
        </p>
        <p>
          We may change prices; a new price applies from your next period, and we'll tell you
          before it does.
        </p>
      </Pasal>

      <Pasal id="availability" judul="8. Availability and changes">
        <p>
          Orisini is provided <strong>as is</strong>. We work to keep it running and to fix failures
          quickly, but we don't guarantee that it will be uninterrupted, that every post will
          publish, or that any feature will keep working on every platform. We may add, change, or
          remove features.
        </p>
      </Pasal>

      <Pasal id="liability" judul="9. Limitation of liability">
        <p>
          To the extent the law allows, Orisini is not liable for indirect or consequential losses,
          lost profits, lost followers, or actions a platform takes against your account. Our total
          liability for any claim is limited to what you paid us in the three months before it
          arose.
        </p>
      </Pasal>

      <Pasal id="ending" judul="10. Ending your account">
        <p>
          You can stop using Orisini at any time, and ask us to delete your account by emailing{' '}
          <Surel />. We may suspend or close an account that breaks these terms; where reasonable,
          we'll tell you why first.
        </p>
      </Pasal>

      <Pasal id="law" judul="11. Governing law">
        <p>These terms are governed by the laws of the Republic of Indonesia.</p>
      </Pasal>

      <Pasal id="changes" judul="12. Changes to these terms">
        <p>
          When these terms change, the effective date above changes too. For significant changes
          we'll email you before they take effect. Continuing to use Orisini after that means you
          accept the new terms.
        </p>
      </Pasal>

      <Pasal id="contact" judul="13. Contact">
        <p>
          Questions about these terms: <Surel />.
        </p>
      </Pasal>
    </HalamanLegal>
  )
}
